import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useClientThreads, useThreadMessages, useClientConversation, type ClientThread } from '@/hooks/useClientThreads';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ArrowLeft, Send, Bot, MessageCircle, Loader2, Reply, Pencil, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmojiPickerButton } from './EmojiPickerButton';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { trackFeatureUsage } from '@/utils/trackFeatureUsage';
import { toast } from '@/hooks/use-toast';
import coachProfileImg from '@/assets/coach-profile.webp';
import { linkify } from '@/utils/linkify';
import { useThreadReactions } from '@/hooks/useMessageReactions';
import { MessageReactions } from './MessageReactions';
import { ChatQuotedPreview, type QuotedMessage } from './ChatQuotedPreview';

interface ChatPanelProps {
  open: boolean;
  onClose: () => void;
}

type PanelMode = 'select' | 'ai' | 'coach';
type AiMessage = { role: 'user' | 'assistant'; content: string };

/* ── AI Help View ── */
function AIHelpView({ onBack, hideHeader = false }: { onBack: () => void; hideHeader?: boolean }) {
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: AiMessage = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('app-assistant', {
        body: { messages: updated },
      });

      if (error) {
        // Check for rate limit / payment errors
        const status = (error as any)?.status;
        if (status === 429) {
          toast({ title: 'Too many requests', description: 'Please wait a moment and try again.', variant: 'destructive' });
        } else if (status === 402) {
          toast({ title: 'Service unavailable', description: 'Please try again later.', variant: 'destructive' });
        }
        throw error;
      }

      const reply = data?.reply || "Sorry, I couldn't process that. Please try again.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      console.error('AI assistant error:', e);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!hideHeader && (
        <div className="p-4 border-b flex items-center gap-3">
          <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium text-foreground">App Assistant</p>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-6 space-y-2">
              <Bot className="h-8 w-8 text-primary/40 mx-auto" />
              <p className="text-sm text-muted-foreground">Ask me anything about using the app!</p>
              <p className="text-xs text-muted-foreground/60">For coaching questions, use your "Inbox" instead.</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={cn(
                "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm",
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-md'
                  : 'bg-secondary text-secondary-foreground rounded-bl-md'
              )}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-2.5">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="p-3 border-t flex gap-2">
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. How do I practice vocabulary?"
          className="min-h-[44px] max-h-[100px] resize-none"
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={!input.trim() || loading}
          className="shrink-0 h-11 w-11"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Mode Selector ── */
function ModeSelector({ onSelect, unreadCount }: { onSelect: (mode: 'ai' | 'coach') => void; unreadCount: number }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-6 space-y-6">
      <div className="text-center space-y-1">
        <p className="text-base font-medium text-foreground">How can we help?</p>
        <p className="text-xs text-muted-foreground">Choose who you'd like to talk to</p>
      </div>
      <div className="w-full space-y-3 max-w-[280px]">
        <button
          onClick={() => onSelect('ai')}
          className="w-full p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left space-y-1.5 group"
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground">App Assistant</span>
          </div>
          <p className="text-xs text-muted-foreground pl-[42px]">
            Questions about how to use features, where to find things, navigation help
          </p>
        </button>

        <button
          onClick={() => onSelect('coach')}
          className={cn(
            "w-full p-4 rounded-xl border transition-all text-left space-y-1.5 group",
            unreadCount > 0
              ? "border-primary bg-primary/5 hover:bg-primary/10"
              : "border-border hover:border-primary/50 hover:bg-primary/5"
          )}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <MessageCircle className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground">Inbox</span>
            {unreadCount > 0 && (
              <Badge className="bg-destructive text-destructive-foreground ml-auto h-5 min-w-[20px] px-1.5 text-[10px]">
                {unreadCount > 9 ? '9+' : unreadCount} new
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground pl-[42px]">
            Messages with Phil, ask questions, share updates, or read his replies
          </p>
        </button>
      </div>
    </div>
  );
}

/* ── Conversation with Phil (single, continuous) ── */
function ConversationView({
  thread,
  onBack,
}: {
  thread: ClientThread;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { messages, loading } = useThreadMessages(thread.id);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<QuotedMessage | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const messageIds = messages.map((m) => m.id);
  const { reactions, toggle: toggleReaction } = useThreadReactions(thread.id, messageIds);
  const reactionsByMessage = reactions.reduce<Record<string, typeof reactions>>((acc, r) => {
    (acc[r.message_id] ||= []).push(r);
    return acc;
  }, {});

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Tick every 30s so the 15-min edit window re-evaluates without a refresh
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const canEdit = (msg: typeof messages[number]) =>
    msg.sender_role === 'client' &&
    msg.sender_id === user?.id &&
    now - new Date(msg.created_at).getTime() < EDIT_WINDOW_MS;
  const canDelete = (msg: typeof messages[number]) =>
    msg.sender_role === 'client' && msg.sender_id === user?.id;

  const startEdit = (msg: typeof messages[number]) => {
    setEditingId(msg.id);
    setEditingText(msg.message_text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const text = editingText.trim();
    if (!text) return;
    setSavingEdit(true);
    const { error } = await supabase
      .from('client_messages')
      .update({ message_text: text, edited_at: new Date().toISOString() } as any)
      .eq('id', editingId);
    setSavingEdit(false);
    if (error) {
      toast({ title: "Couldn't update message", description: error.message, variant: 'destructive' });
      return;
    }
    cancelEdit();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const id = deleteId;
    setDeleteId(null);
    const { error } = await supabase.from('client_messages').delete().eq('id', id);
    if (error) {
      toast({ title: "Couldn't delete message", description: error.message, variant: 'destructive' });
    }
  };

  const senderLabelFor = (role: 'client' | 'coach') =>
    role === 'client' ? 'You' : 'Phil';

  const scrollToMessage = (id: string) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashId(id);
    setTimeout(() => setFlashId(prev => (prev === id ? null : prev)), 1500);
  };

  const startReplyTo = (msg: typeof messages[number]) => {
    setReplyingTo({
      id: msg.id,
      sender_role: msg.sender_role,
      message_text: msg.message_text,
      voice_url: msg.voice_url,
    });
    textareaRef.current?.focus();
  };

  const handleReply = async () => {
    if (!reply.trim() || !user) return;
    setSending(true);
    await supabase.from('client_messages').insert({
      thread_id: thread.id,
      sender_role: 'client',
      sender_id: user.id,
      message_text: reply.trim(),
      reply_to_id: replyingTo?.id ?? null,
    } as any);

    trackFeatureUsage('ask_phil', 'follow_up');
    setReply('');
    setReplyingTo(null);
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b flex items-center gap-3">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <img src={coachProfileImg} alt="Phil" className="h-8 w-8 rounded-full object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground line-clamp-1">Phil</p>
          <p className="text-[11px] text-muted-foreground">Your ongoing conversation</p>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {!loading && messages.length === 0 && (
            <div className="text-center py-8 space-y-1">
              <p className="text-sm text-muted-foreground">No messages yet.</p>
              <p className="text-xs text-muted-foreground/70">Send Phil a message, a link, or a quick update.</p>
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg.id}
              ref={(el) => { messageRefs.current[msg.id] = el; }}
              className={cn(
                "group flex gap-2 transition-colors rounded-xl -mx-1 px-1 py-0.5",
                msg.sender_role === 'client' ? 'justify-end' : 'justify-start',
                flashId === msg.id && 'bg-primary/10',
              )}
            >
              {msg.sender_role !== 'client' && (
                <img src={coachProfileImg} alt="Phil" className="h-7 w-7 rounded-full object-cover shrink-0 mt-1" />
              )}
              {msg.sender_role === 'client' && (
                <div className="self-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
                  {canEdit(msg) && (
                    <button
                      type="button"
                      onClick={() => startEdit(msg)}
                      className="h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center"
                      aria-label="Edit message"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {canDelete(msg) && (
                    <button
                      type="button"
                      onClick={() => setDeleteId(msg.id)}
                      className="h-7 w-7 rounded-full bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex items-center justify-center"
                      aria-label="Delete message"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => startReplyTo(msg)}
                    className="h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center"
                    aria-label="Reply to message"
                  >
                    <Reply className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <div className="flex flex-col max-w-[80%] min-w-0">
              <div className={cn(
                "rounded-2xl px-4 py-2.5 text-sm",
                msg.sender_role === 'client'
                  ? 'bg-primary text-primary-foreground rounded-br-md self-end'
                  : 'bg-secondary text-secondary-foreground rounded-bl-md self-start'
              )}>
                {(msg.reply_to_id || msg.reply_to) && (
                  <div className="mb-1.5">
                    <ChatQuotedPreview
                      message={msg.reply_to ?? null}
                      senderLabel={
                        msg.reply_to
                          ? senderLabelFor(msg.reply_to.sender_role)
                          : 'Message'
                      }
                      variant="bubble"
                      onPrimary={msg.sender_role === 'client'}
                      onClick={msg.reply_to ? () => scrollToMessage(msg.reply_to!.id) : undefined}
                    />
                  </div>
                )}
                {editingId === msg.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="min-h-[60px] text-sm bg-background text-foreground"
                      rows={2}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          void saveEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 px-2 text-xs">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={saveEdit} disabled={savingEdit || !editingText.trim()} className="h-7 px-2 text-xs">
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap break-words">{linkify(msg.message_text)}</p>
                )}
                {msg.button_label && msg.button_route && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 w-full bg-background text-foreground"
                    onClick={() => navigate(msg.button_route!)}
                  >
                    {msg.button_label}
                  </Button>
                )}
                <p className={cn(
                  "text-[10px] mt-1",
                  msg.sender_role === 'client' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                )}>
                  {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {msg.edited_at && <span className="ml-1 italic">(edited)</span>}
                </p>
              </div>
              <MessageReactions
                messageId={msg.id}
                reactions={reactionsByMessage[msg.id] ?? []}
                onToggle={toggleReaction}
                alignEnd={msg.sender_role === 'client'}
              />
              </div>
              {msg.sender_role !== 'client' && (
                <button
                  type="button"
                  onClick={() => startReplyTo(msg)}
                  className="self-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity h-7 w-7 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center shrink-0"
                  aria-label="Reply to message"
                >
                  <Reply className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="p-3 border-t space-y-2">
          {replyingTo && (
            <ChatQuotedPreview
              message={replyingTo}
              senderLabel={`Replying to ${senderLabelFor(replyingTo.sender_role)}`}
              variant="composer"
              onCancel={() => setReplyingTo(null)}
              onClick={() => scrollToMessage(replyingTo.id)}
            />
          )}
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Message Phil…"
              className="min-h-[44px] max-h-[180px] resize-none"
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
              }}
            />
            <EmojiPickerButton textareaRef={textareaRef} setValue={setReply} className="h-11 w-11" />
            <Button
              size="icon"
              onClick={handleReply}
              disabled={!reply.trim() || sending}
              className="shrink-0 h-11 w-11"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the message from the conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ── Loads (or creates) the client's single conversation ── */
function CoachConversation({ onBack }: { onBack: () => void }) {
  const { thread, loading, markRead } = useClientConversation();

  useEffect(() => {
    if (thread) markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  if (loading || !thread) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <ConversationView thread={thread} onBack={onBack} />;
}

/* ── Main ChatPanel ── */
export function ChatPanel({ open, onClose }: ChatPanelProps) {
  const isMobile = useIsMobile();
  const { unreadCount } = useClientThreads();
  const [mode, setMode] = useState<PanelMode>('select');

  // Reset when opening
  useEffect(() => {
    if (open) {
      setMode('select');
    }
  }, [open]);

  const content = (
    <div className="h-full flex flex-col">
      {mode === 'select' && (
        <ModeSelector unreadCount={unreadCount} onSelect={setMode} />
      )}
      {mode === 'ai' && (
        <AIHelpView onBack={() => setMode('select')} hideHeader={isMobile} />
      )}
      {mode === 'coach' && <CoachConversation onBack={() => setMode('select')} />}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()} repositionInputs={false}>
        <DrawerContent className="max-h-[85dvh] flex flex-col">
          <DrawerHeader className="pb-2 shrink-0">
            <div className="flex items-center gap-2">
              {mode !== 'select' && (
                <button onClick={() => setMode('select')} className="text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <DrawerTitle>{mode === 'ai' ? 'App Assistant' : mode === 'coach' ? 'Ask Phil' : 'Chat'}</DrawerTitle>
            </div>
            <DrawerDescription>
              {mode === 'ai' ? 'Ask about app features and navigation' : mode === 'coach' ? 'One continuous conversation with Phil. Scroll back anytime.' : 'Choose how you\'d like to get help'}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-hidden min-h-0" style={{ height: '60dvh' }}>
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] p-0 flex flex-col">
        <SheetHeader className="p-4 pb-2 border-b">
          <SheetTitle>{mode === 'ai' ? 'App Assistant' : mode === 'coach' ? 'Ask Phil' : 'Chat'}</SheetTitle>
          <SheetDescription>
            {mode === 'ai' ? 'Ask about app features and navigation' : mode === 'coach' ? 'One continuous conversation with Phil. Scroll back anytime.' : 'Choose how you\'d like to get help'}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-hidden">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
}
